<template>
    <b-card header="Multi-Factor Authentication"
        header-bg-variant="info"
        header-text-variant="white"
        class="mfa"
    >
        <h3>Protect your account with Multi-Factor Authentication</h3>

        <p class="byline">​Multi-Factor Authentication (MFA) is an identity verification method in which a user must supply at least 2 pieces of evidence, such as their password and a temporary passcode, to prove their identity.</p>

        <div>
            <b-alert class="warning-border" variant="warning" show v-if="!this.smsAuth && !this.emailAuth">At least one Multi-Factor Authentication option must be enabled</b-alert>

            <b-table-simple borderless responsive>
                <b-tr>
                    <b-th>Multi-Factor Authentication Options</b-th>
                    <b-th>Status</b-th>
                    <b-th>Action</b-th>
                </b-tr>
               
                <b-tr>
                    <b-td>
                        <div>
                            <h4 class="default">Email <span v-if="this.user.social_provider_default_method === 'email'">(Default)</span></h4>
                            <p>Receive an email to generate a one-time security code</p>
                        </div>
                    </b-td>
                    <b-td>
                        <div v-if="this.emailAuth">
                            <strong>Enabled</strong>
                        </div>
                        <div v-else>
                            <strong>Disabled</strong>
                        </div>
                    </b-td>
                    <b-td>
                        <div>
                            <ul class="actions">
                            </ul>
                        </div>
                    </b-td>
                </b-tr>

                <b-tr>
                    <b-td>
                        <div>
                            <h4>Text Message <span v-if="this.user.social_provider_default_method === 'sms'">(Default)</span></h4>
                            <p>Receive a text message to generate a one-time security code.</p>
                        </div>
                    </b-td>
                    <b-td>
                        <div v-if="this.smsAuth">
                            <strong>Enabled</strong>
                        </div>
                        <div v-else>
                            <strong>Disabled</strong>
                        </div>
                    </b-td>
                    <b-td>
                        <div>
                            <ul class="actions">
                                <li v-if="!this.smsAuth">
                                    <a href="#/" variant="success" @click="openSMSModal">Enable</a>
                                </li>
                                <li v-if="this.smsAuth">
                                    <a href="#/" @click="disableSMS">Disable</a>
                                </li>
                                <li :class="this.smsAuth ? 'border' : 'no-border'" v-if="this.smsAuth">
                                    <a href="#/" variant="success" @click="openSMSModal">Edit Phone</a>
                                </li>
                                <li class="border">
                                    <span v-if="this.phone_number && this.smsAuth">{{ country_code }} {{ formattedPhoneNumber }} </span>
                                </li>
                            </ul>
                        </div>
                    </b-td>
                </b-tr>
            </b-table-simple>
        </div>

        <b-modal title="Set up phone number via text message" v-model="displayEditPhoneModal">
            <b-container fluid>
                <b-row>
                    <b-col lg="12">
                        <div>
                            <p style="font-size: 0.8em;">Enter your phone number to receive a text message as an MFA method.</p>
                            <b-form-group label="COUNTRY/REGION *" label-for="country-code">
                                <b-form-select v-model="country_code" :options="country_code_options"></b-form-select>
                            </b-form-group>

                            <b-form-group label="PHONE NUMBER *" label-for="phone-number">
                                <b-form-input 
                                    v-model="phone_number" 
                                    id="phone-number"
                                    :state="isPhoneValid"
                                />
                                <b-form-invalid-feedback>
                                    Please enter a valid phone number (10 digits).
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </div>
                    </b-col>
                </b-row>
            </b-container>
            <div slot="modal-footer">
                <b-btn variant="default" @click=" displayEditPhoneModal = false">Cancel</b-btn>
                <b-btn variant="info" @click="saveAndEnableSMS()" :disabled="!isPhoneValid">Continue</b-btn>
            </div>
        </b-modal>
    </b-card>
</template>

<script>
    export default {
        props: {
            'user': {
                type: Object,
                required: true
            },
            'sms_phone_number': {
                type: [String],
                required: false,
                default: null
            },
            'is_mfa_enabled': {
                type: [String, Boolean, Number],
                required: false,
                default: null   
            }
        },

        data() {
            return {
                mfaForm: new Form({
                    email: this.emailAuth,
                    microsoftAuth: this.microsoftAuth,
                    smsAuth: this.smsAuth,
                    country_code: this.country_code,
                    phone_number: this.phone_number,
                    user_id: 0
                }),
                updateSMSForm: new Form({
                    user_id: 0,
                    country_code: '1',
                    phone_number: '',
                }),
                country_code_options: [
                    { value: '1', text: '+1 (United States)' },
                ],
                emailAuth: true,
                smsAuth: false, 
                isSMSDefault: false,
                phone_number: '', 
                displayEditPhoneModal: false,
                country_code: 1,
                phone_number: '',
            }
        },

        methods: {
            submit() {
                console.log("Submitting MFA Form");

                this.mfaForm.smsAuth = this.smsAuth;
                this.mfaForm.user_id = this.user.id;

                console.debug("mfaForm", this.mfaForm);
                this.mfaForm.submit('patch', '/profile/mfa-settings');
            },

            setSMSPhone() {
                console.log("Set SMS Phone");
                this.displayEditPhoneModal = true;
            },

            openSMSModal() {
                console.log("Open SMS Modal");
                this.displayEditPhoneModal = true;
            },

            saveAndEnableSMS() {
                try {
                    this.smsAuth = true;

                    this.mfaForm.smsAuth = true;
                    this.mfaForm.user_id = this.user.id;
                    this.mfaForm.country_code = this.country_code;
                    this.mfaForm.phone_number = this.phone_number;

                    this.updateSMSForm.phone_number = this.phone_number;
                    this.updateSMSForm.user_id = this.user.id;
                    this.updateSMSForm.country_code = this.country_code;

                    console.debug("mfaForm", this.mfaForm);

                    this.mfaForm.submit('patch', '/profile/mfa-set-sms-phone')
                    this.displayEditPhoneModal = false;
                } catch (error) {
                    // Handle network errors
                    console.debug("Something's gone wrong", debug);
                }
            },

           disableSMS() {
                console.log("Disable SMS Start");

                // New logic (uses axios)
                try {
                    axios.patch('/profile/mfa-disable-sms', {
                        user_id: this.user.id
                    })
                    .then(response => {
                        console.log("response", response);
                        if (response.data.data === true) {
                            this.smsAuth = false;
                        }
                    })
                    .catch(err => {
                        console.log("Failed", err);
                    });
                } catch (error) {
                    console.debug("Something's gone wrong", error);
                    this.smsAuth = true;
                }

                console.log("Disable SMS End");
            },
        },


        mounted() {
            console.log("Updating MFA Settings");
            console.debug("Prop SMS Number: ", this.sms_phone_number);
            console.debug("Prop MFA Enabled:", this.is_mfa_enabled);

            console.log("Mounted pre SMS number");
            if (this.sms_phone_number && this.sms_phone_number !== "00000000000") {
                console.debug("Prop Phone Number", this.sms_phone_number);
                console.debug("Value of eval", this.sms_phone_number !== "00000000000");
                
                // Split into its elements
                const digits = String(this.sms_phone_number).replace(/\D/g, '');

                // Extract parts of the phone number
                const countryCode = digits.substring(0, 1);
                const areaCode = digits.substring(1, 4);
                const prefix = digits.substring(4, 7);
                const lineNumber = digits.substring(7, 11);
                
                // Format with country code
                this.country_code = countryCode;
                this.phone_number = `${areaCode}-${prefix}-${lineNumber}`;

                this.smsAuth = true;
            }
        },

        computed: {
            formattedPhoneNumber() {
                if (!this.phone_number) return '';
                
                // Strip all non-numeric characters
                const cleanNumber = this.phone_number.replace(/\D/g, '');
                
                // Format as (XXX) XXX-XXXX if it has enough digits
                if (cleanNumber.length >= 10) {
                    return `(${cleanNumber.slice(0,3)}) ${cleanNumber.slice(3,6)}-${cleanNumber.slice(6,10)}`;
                }
                
                // Return the cleaned number if it doesn't have enough digits for formatting
                return cleanNumber;
            },

            isPhoneValid() {
                // Count only digits in the input
                const digitCount = this.phone_number.replace(/\D/g, '').length;
                return digitCount == 10; // >=
            }
        }
    }
</script>

<style>
    .disabled {
        position: relative;
        z-index: 1;
    }

    .disabled .blocker {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 10;
        font-size: 1.5em;
        font-weight: bold;
        background: rgba(0, 0, 0, 0.5);
        cursor: not-allowed;
        padding: .3em 1em 0;
        text-align: center;
        color: #fff;
        width: 100%;
        height: 100%;
    }

    .warning-border {
        border: 2px solid #000;
    }

    .mfa h3 {
        color: #4E7590;
        font-size: 1em;
        font-weight: bold;
        padding: 0;
        margin-bottom: 0;
    }

    .mfa .byline {
        color: #4E7590;
        font-size: .9em;
        margin: 0 0 1em;
        padding: 0;
    }

    .mfa th {
        color: #4E7590;
        font-size: 1.25em;
        font-weight: bold;
        margin-bottom: 0;
    }

    .mfa td {
        color: #A4A4A4;
    }

    .mfa td h4 {
        margin: 0;
        font-weight: bold;
    }

    .mfa td div strong {
        font-weight: bold;
    }

    .mfa td p {
        font-size: .9em;
        padding: 0;
        margin: 0;
    }

    .mfa .actions {

    }

    .mfa .actions {
        list-style-type: none;
        margin: 0;
        padding: 0;
    }

    .mfa .actions li {
        display: inline-block;
        padding-right: 10px;
    }

    .mfa .actions li.border {
        border: 0 !important;
        border-left: 2px solid #000 !important;
        padding-left: 10px;
    }

    .mfa .actions li:last-child {
        padding-right: 0;
    }
</style>